import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";

const CHAPTERS = [
  {
    n: "01",
    title: "Il laboratorio",
    text: "Ogni giorno impastiamo, lieviti­amo e sforniamo nel nostro laboratorio a Chiarano. Nessuna scorciatoia: tempo, pazienza e mani esperte.",
  },
  {
    n: "02",
    title: "Gli ingredienti",
    text: "Materie prime selezionate e genuine. Scegliamo qualità perché è la qualità che si deve sentire in ogni morso, dal croissant al pane.",
  },
  {
    n: "03",
    title: "Le idee",
    text: "Dai pasticcini ai biscotti, fino al pane: ogni giorno mettiamo impegno e passione per creare prodotti artigianali unici.",
  },
];

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="chi-siamo"
      ref={ref}
      className="bg-[#FDFBF7] py-16 sm:py-20"
      data-testid="chi-siamo-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="font-script text-3xl sm:text-4xl text-[#8C9B86] mb-4"
            >
              Il salottino buono del paese e non solo
            </motion.p>
            <motion.h2
              variants={fade}
              custom={0}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="font-display font-light tracking-tight leading-[0.95] text-[clamp(2.5rem,6vw,5rem)] text-[#2A1B14]"
            >
              Fatto a mano,
              <br />
              <span className="italic">ogni mattina.</span>
            </motion.h2>

            <motion.div
              variants={fade}
              custom={1}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mt-10 arch-frame aspect-[4/5] max-w-sm"
            >
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={IMAGES.heroPoster}
                alt="Il pasticcere al lavoro, piegato sul tavolo del laboratorio"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              variants={fade}
              custom={2}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mt-6 max-w-sm"
            >
              <a
                href="https://www.instagram.com/p/DZaij0qCoaU/"
                target="_blank"
                rel="noreferrer"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#2A1B14] text-[#FDFBF7] px-6 py-3 text-base font-medium hover:bg-[#3a271d] hover:scale-[0.98] transition-all duration-300"
                data-testid="chi-siamo-discover-btn"
              >
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                Dietro le quinte
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-7 lg:pt-8">
            <div className="space-y-14">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.n}
                  variants={fade}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-100px" }}
                  className="grid grid-cols-[auto_1fr] gap-6 sm:gap-10 border-t border-[#E8E3D9] pt-8"
                  data-testid={`chapter-${c.n}`}
                >
                  <span className="font-display text-4xl sm:text-6xl text-[#D4AF37] leading-none">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-display text-3xl sm:text-4xl text-[#2A1B14] mb-3">
                      {c.title}
                    </h3>
                    <p className="font-body text-[#5C4F4A] text-base sm:text-lg leading-relaxed max-w-xl">
                      {c.text}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
